import { Icon } from "@iconify/react";
import { Close, ExpandMore } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { primary, secondary } from "../../../../themes/theme";
import "../../pages/navbar/MainNavBar.css";

interface ProfileDrawerContentProps {
  user: {
    imageUrl?: string;
    username?: string;
    email?: string;
    isAdmin?: boolean;
    isSuperAdmin?: boolean;
  };
  handleProfileDrawerClose: () => void;
  handleLogout: () => void;
  isAuthenticated: boolean;
  TokenUtil: any;
}

const ProfileDrawerContent: React.FC<ProfileDrawerContentProps> = ({
  user,
  handleProfileDrawerClose,
  handleLogout,
  isAuthenticated,
  TokenUtil,
}) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(true);

  if (
    !TokenUtil.isLoggedIn() ||
    !isAuthenticated ||
    !user ||
    Object.keys(user).length === 0
  ) {
    return null;
  }

  const handleExpansion = () => {
    setExpanded((prevExpanded) => !prevExpanded);
  };

  const gradientBorderClass2 = user.isSuperAdmin
    ? "gradient-border2 super-admin"
    : user.isAdmin
    ? "gradient-border2 admin"
    : "gradient-border2";

  const listItemStyle = {
    fontFamily: "Ubuntu",
    color: secondary[700],
    fontWeight: "450",
    cursor: "pointer",
    borderRadius: "10px",
    mb: 1,
    "&:hover": {
      background: secondary[200],
      color: secondary[800],
    },
  };

  const listItemStyle1 = {
    fontFamily: "Ubuntu",
    color: secondary[700],
    fontWeight: "450",
    cursor: "pointer",
    borderRadius: "10px",
    mb: 1,
    "&:hover": {
      background: secondary[50],
      color: secondary[800],
    },
  };

  return (
    <Box sx={{ p: 1, m: 1, position: "relative" }}>
      <Box
        sx={{
          position: "sticky",
          top: 4,
          zIndex: 1500,
          background: "transparent",
          backdropFilter: "blur(10px)",
          borderRadius: "15px",
        }}
      >
        <Box
          sx={{
            mt: 1,
            mx: "auto",
            display: "flex",
            justifyContent: "flex-start",
            height: "100%",
            alignItems: "center",
          }}
        >
          <IconButton
            onClick={handleProfileDrawerClose}
            sx={{
              background: "transparent",
              color: "grey",
              "&:hover": {
                background: "transparent",
                color: primary[900],
              },
            }}
          >
            <Close />
          </IconButton>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignContent: "center",
            alignItems: "center",
            gap: "6px",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontFamily: "Ubuntu",
              fontWeight: "bold",
              color: user?.isSuperAdmin
                ? "#00c91e"
                : user?.isAdmin
                ? "#e33d00"
                : "",
              mt: 1,
              textTransform: "uppercase",
            }}
          >
            {user?.isSuperAdmin ? "Super Admin" : user?.isAdmin ? "Admin" : ""}
          </Typography>
          <div className={gradientBorderClass2}>
            <img
              src={user?.imageUrl}
              alt=""
              style={{ border: "3px solid white" }}
            />
          </div>
          <Typography
            variant="body1"
            sx={{
              fontFamily: "Ubuntu",
              fontWeight: "bold",
              color: secondary[900],
              mt: 1,
              textTransform: "uppercase",
            }}
          >
            {user?.username}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontFamily: "Ubuntu",
              fontWeight: "450",
              color: secondary[700],
              mb: 1,
            }}
          >
            {user?.email}
          </Typography>
        </Box>
      </Box>
      {TokenUtil.isLoggedIn() && user.isAdmin && (
        <>
          <Divider sx={{ my: 2, border: `1px dashed ${secondary[300]}` }} />
          <Box>
            <Accordion
              defaultExpanded
              elevation={0}
              expanded={expanded}
              onChange={handleExpansion}
              sx={{
                background: secondary[100],
                borderRadius: "15px",

                "& .MuiAccordion-region": {
                  height: expanded ? "auto" : 0,
                  borderRadius: "15px",
                },
                "& .MuiAccordionDetails-root": {
                  display: expanded ? "block" : "none",
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls="panel1-content"
                id="panel1-header"
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-start",
                    height: "100%",
                    alignItems: "center",
                  }}
                >
                  <Icon
                    icon={
                      user?.isSuperAdmin
                        ? "solar:shield-user-bold-duotone"
                        : user?.isAdmin
                        ? "solar:shield-user-line-duotone"
                        : ""
                    }
                    width="22"
                    height="22"
                    style={{
                      color: user?.isSuperAdmin
                        ? "#00c91e"
                        : user?.isAdmin
                        ? "#e33d00"
                        : "",
                    }}
                  />
                  <Typography
                    sx={{
                      fontFamily: "Ubuntu",
                      fontWeight: "500",
                      color: secondary[800],
                      ml: 1,
                    }}
                  >
                    {user?.isSuperAdmin
                      ? "Super Admin Actions"
                      : user?.isAdmin
                      ? "Admin Actions"
                      : ""}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <List>
                  <ListItem
                    sx={{ ...listItemStyle1 }}
                    onClick={() => navigate("/categories/add")}
                  >
                    <ListItemIcon>
                      <Icon
                        icon="icon-park-outline:list-add"
                        width="24"
                        height="24"
                      />
                    </ListItemIcon>
                    <ListItemText>Add Categories</ListItemText>
                  </ListItem>
                  <ListItem
                    sx={{ ...listItemStyle1 }}
                    onClick={() => navigate("/products/upload")}
                  >
                    <ListItemIcon>
                      <Icon icon="uim:upload-alt" width="24" height="24" />
                    </ListItemIcon>
                    <ListItemText>Upload Products</ListItemText>
                  </ListItem>
                  {user.isSuperAdmin && (
                    <>
                      <Divider sx={{ mb: 1 }} />
                      <ListItem
                        sx={{ ...listItemStyle1 }}
                        onClick={() => navigate("/products/admin")}
                      >
                        <ListItemIcon>
                          <Icon
                            icon="oui:app-index-management"
                            width="24"
                            height="24"
                          />
                        </ListItemIcon>
                        <ListItemText>Manage Products</ListItemText>
                      </ListItem>
                      <ListItem
                        sx={{ ...listItemStyle1 }}
                        onClick={() => navigate("/orders/admin")}
                      >
                        <ListItemIcon>
                          <Icon
                            icon="material-symbols:manage-search-rounded"
                            width="24"
                            height="24"
                          />
                        </ListItemIcon>
                        <ListItemText>Manage Orders</ListItemText>
                      </ListItem>
                      <ListItem
                        sx={{ ...listItemStyle1 }}
                        onClick={() => navigate("/users/admin")}
                      >
                        <ListItemIcon>
                          <Icon
                            icon="material-symbols:manage-accounts-rounded"
                            width="24"
                            height="24"
                          />
                        </ListItemIcon>
                        <ListItemText>Manage Users</ListItemText>
                      </ListItem>
                    </>
                  )}
                </List>
              </AccordionDetails>
            </Accordion>
          </Box>
        </>
      )}

      <Divider sx={{ my: 2, border: `1px dashed ${secondary[300]}` }} />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          alignContent: "space-between",
          height: "100%",
          mx: "auto",
        }}
      >
        <List>
          <ListItem sx={{ ...listItemStyle }} onClick={() => navigate("/")}>
            <ListItemIcon>
              <Icon icon="lets-icons:home-duotone" width="24" height="24" />
            </ListItemIcon>
            <ListItemText>Home</ListItemText>
          </ListItem>
          <ListItem
            sx={{ ...listItemStyle }}
            onClick={() => navigate("/users/profile")}
          >
            <ListItemIcon>
              <Icon icon="hugeicons:profile-02" width="24" height="24" />
            </ListItemIcon>
            <ListItemText>Profile</ListItemText>
          </ListItem>
          <ListItem
            sx={{ ...listItemStyle }}
            onClick={() => navigate("/users/change-password")}
          >
            <ListItemIcon>
              <Icon
                icon="solar:lock-password-bold-duotone"
                width="24"
                height="24"
              />
            </ListItemIcon>
            <ListItemText>Change Password</ListItemText>
          </ListItem>
          <ListItem
            sx={{ ...listItemStyle }}
            onClick={() => navigate("/users/orders/me")}
          >
            <ListItemIcon>
              <Icon icon="solar:cart-3-bold-duotone" width="24" height="24" />
            </ListItemIcon>
            <ListItemText>Orders</ListItemText>
          </ListItem>
          <ListItem
            sx={{
              ...listItemStyle,
              color: "orangered",
              "& .logoutIcon": {
                color: "orangered",
              },
              "&:hover": {
                background: "#e33d0020",
                color: "red",
                fontWeight: "550",
                "& .logoutIcon": {
                  color: "red",
                },
              },
            }}
            onClick={() => handleLogout()}
          >
            <ListItemIcon>
              <Icon
                className="logoutIcon"
                icon="solar:logout-3-bold-duotone"
                width="24"
                height="24"
              />
            </ListItemIcon>
            <ListItemText>Logout</ListItemText>
          </ListItem>
        </List>
      </Box>
      <Divider sx={{ my: 2, border: `1px dashed ${secondary[300]}` }} />
    </Box>
  );
};

export default ProfileDrawerContent;
